import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle
} from '../components/ui/card';
import { 
  Plus,
  Edit,
  Save,
  X,
  Trash2
} from 'lucide-react';
import { formatCurrency } from '../utils/chartUtils';
import { sowAPI, clientsAPI, resourcesAPI, resourceTypesAPI } from '../services/api';

import toast from 'react-hot-toast';
import errorHandler from '../utils/errorHandler';

const SOWManagement = () => {
  const [filteredData, setFilteredData] = useState([]);

  const [editingSOW, setEditingSOW] = useState(null);
  const [newSOW, setNewSOW] = useState({
    client_id: '',
    resource_id: '',
    resource_type_id: '',
    month_year: '',
    planned_days: 0,
    utilization_rate: 1.0,
    day_rate: 0,
    forecast_revenue: 0,
    frequency: 'monthly'
  });
  const [clients, setClients] = useState([]);
  const [resources, setResources] = useState([]);
  const [resourceTypes, setResourceTypes] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Filter states
  const [filters, setFilters] = useState({
    month: '',
    client: '',
    resourceType: '',
    resource: ''
  });
  const [availableMonths, setAvailableMonths] = useState([]);

  // Generate last 12 months dynamically
  const generateLast12Months = () => {
    const months = [];
    const currentDate = new Date();
    
    for (let i = 0; i < 12; i++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const monthName = date.toLocaleString('default', { month: 'long' });
      
      months.push({
        value: `${year}-${month}`,
        label: `${monthName} ${year}`
      });
    }
    
    return months;
  };

  // Get last month as default
  const getLastMonth = () => {
    const currentDate = new Date();
    const lastMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
    const year = lastMonth.getFullYear();
    const month = String(lastMonth.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  };

  useEffect(() => {
    // Initialize months and set default to last month
    const months = generateLast12Months();
    setAvailableMonths(months);
    
    if (!filters.month) {
      const lastMonth = getLastMonth();
      setFilters(prev => ({ ...prev, month: lastMonth }));
    }
    
    if (!newSOW.month_year) {
      const lastMonth = getLastMonth();
      setNewSOW(prev => ({ ...prev, month_year: lastMonth }));
    }
    
    loadFormData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-calculate forecast revenue when planned days, day rate, utilization rate, or frequency changes (for new SOW)
  // Formula: PlannedDays * utilizationRate * DayRate * (Monthly/Weekly multiplier)
  // SOW default utilization is 100% (editable in UI)
  useEffect(() => {
    const utilizationRate = newSOW.utilization_rate ?? 1.0;
    let revenue = newSOW.planned_days * utilizationRate * newSOW.day_rate;
    
    // If weekly, multiply by 4 to get monthly equivalent
    if (newSOW.frequency === 'weekly') {
      revenue = revenue * 4;
    }
    
    revenue = Math.round(revenue);
    setNewSOW(prev => ({ ...prev, forecast_revenue: revenue }));
  }, [newSOW.planned_days, newSOW.day_rate, newSOW.utilization_rate, newSOW.frequency]);

  // Auto-calculate forecast revenue when planned days, day rate, utilization rate, or frequency changes (for editing SOW)
  // Formula: PlannedDays * utilizationRate * DayRate * (Monthly/Weekly multiplier)
  useEffect(() => {
    if (editingSOW) {
      const utilizationRate = editingSOW.utilization_rate ?? 1.0;
      let revenue = editingSOW.planned_days * utilizationRate * editingSOW.day_rate;
      
      // If weekly, multiply by 4 to get monthly equivalent
      if (editingSOW.frequency === 'weekly') {
        revenue = revenue * 4;
      }
      
      revenue = Math.round(revenue);
      setEditingSOW(prev => ({ ...prev, forecast_revenue: revenue }));
    }
  }, [editingSOW?.planned_days, editingSOW?.day_rate, editingSOW?.utilization_rate, editingSOW?.frequency]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load data when filters change
  useEffect(() => {
    loadGroupedDataWithFilters();
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadGroupedDataWithFilters = async () => {
    const filterParams = {};
    
    if (filters.month) {
      filterParams.month = filters.month;
    }
    
    if (filters.client) {
      filterParams.clientId = filters.client;
    }
    
    if (filters.resourceType) {
      filterParams.resourceTypeId = filters.resourceType;
    }
    
    if (filters.resource) {
      filterParams.resourceId = filters.resource;
    }
    
    await loadGroupedData(filterParams);
  };

  const loadGroupedData = async (filterParams = {}) => {
    try {
      setLoading(true);
      const response = await sowAPI.getAll(filterParams);
      console.log('SOW data loaded:', response.data);
      setFilteredData(response.data);
    } catch (error) {
      console.error('Error loading SOW data:', error);
      toast.error('Failed to load SOW data');
    } finally {
      setLoading(false);
    }
  };

  const loadFormData = async () => {
    try {
      const [clientsRes, resourcesRes, resourceTypesRes] = await Promise.all([
        clientsAPI.getAll(),
        resourcesAPI.getAll(),
        resourceTypesAPI.getAll()
      ]);
      
      console.log('Form data loaded:', {
        clients: clientsRes.data,
        resources: resourcesRes.data,
        resourceTypes: resourceTypesRes.data
      });
      
      setClients(Array.isArray(clientsRes.data) ? clientsRes.data : []);
      setResources(Array.isArray(resourcesRes.data) ? resourcesRes.data : []);
      setResourceTypes(Array.isArray(resourceTypesRes.data) ? resourceTypesRes.data : []);
    } catch (error) {
      console.error('Error loading form data:', error);
      toast.error('Failed to load form data');
    }
  };




  const handleEditSOW = (sow) => {
    setEditingSOW({
      ...sow,
      originalData: { ...sow }
    });
  };

  const handleSaveSOW = async (sow) => {
    try {
      const sowData = {
        plannedDays: parseFloat(sow.planned_days),
        utilizationRate: parseFloat(sow.utilization_rate) || 0.9,
        dayRate: Math.round(parseFloat(sow.day_rate)),
        forecastRevenue: Math.round(parseFloat(sow.forecast_revenue)),
        frequency: sow.frequency,
        notes: sow.notes || ''
      };
      
      await sowAPI.update(sow.id, sowData);
      setEditingSOW(null);
      toast.success('SOW updated successfully');
      loadGroupedDataWithFilters(); // Refresh data with current filters
    } catch (error) {
      console.error('Error updating SOW:', error);
      const errorMessage = errorHandler.getErrorMessage(error);
      toast.error('Failed to update SOW: ' + errorMessage);
    }
  };

  const handleCancelEdit = () => {
    setEditingSOW(null);
  };

  // Function to get day rate from existing SOW data
  const getSowDayRate = (clientId, resourceId, resourceTypeId) => {
    if (!clientId) return 0;
    
    // First, try to find exact client-resource match
    if (resourceId) {
      const sow = filteredData.find(s => 
        s.client_id === parseInt(clientId) && 
        s.resource_id === parseInt(resourceId)
      );
      if (sow && sow.day_rate) return sow.day_rate;
    }
    
    // If no exact match, try to find client-resourceType match
    if (resourceTypeId) {
      const sow = filteredData.find(s => 
        s.client_id === parseInt(clientId) && 
        s.resource_type_id === parseInt(resourceTypeId)
      );
      if (sow && sow.day_rate) return sow.day_rate;
    }
    
    // If still no match, try to find any SOW for this client with the same resource type
    if (resourceTypeId) {
      const sow = filteredData.find(s => 
        s.client_id === parseInt(clientId) && 
        s.resource_type_id === parseInt(resourceTypeId)
      );
      if (sow && sow.day_rate) return sow.day_rate;
    }
    
    return 0;
  };

  const handleClientChange = (clientId) => {
    setNewSOW(prev => ({ 
      ...prev, 
      client_id: clientId,
      day_rate: getSowDayRate(clientId, prev.resource_id, prev.resource_type_id)
    }));
  };

  const handleResourceChange = (resourceId) => {
    const selectedResource = resources.find(r => r.id === parseInt(resourceId));
    if (selectedResource) {
      const resourceType = resourceTypes.find(rt => rt.id === parseInt(selectedResource.resource_type_id));
      setNewSOW(prev => ({
        ...prev,
        resource_id: resourceId,
        day_rate: getSowDayRate(prev.client_id, resourceId, selectedResource.resource_type_id),
        resource_type_id: selectedResource.resource_type_id || '',
        utilization_rate: resourceType ? (parseFloat(resourceType.utilization_rate) || 0.9) : 0.9
      }));
    } else {
      setNewSOW(prev => ({ 
        ...prev, 
        resource_id: resourceId,
        day_rate: getSowDayRate(prev.client_id, resourceId, prev.resource_type_id)
      }));
    }
  };

  const handleResourceTypeChange = (resourceTypeId) => {
    const resourceType = resourceTypes.find(rt => rt.id === parseInt(resourceTypeId));
    setNewSOW(prev => ({ 
      ...prev, 
      resource_type_id: resourceTypeId,
      day_rate: getSowDayRate(prev.client_id, prev.resource_id, resourceTypeId),
      utilization_rate: resourceType ? (parseFloat(resourceType.utilization_rate) || 0.9) : 0.9
    }));
  };

  const handleAddSOW = async () => {
    try {
      // Validate that either resource or resource type is selected
      if (!newSOW.resource_id && !newSOW.resource_type_id) {
        toast.error('Please select either a specific resource or a resource type');
        return;
      }

      const sowData = {
        clientId: parseInt(newSOW.client_id),
        resourceId: newSOW.resource_id ? parseInt(newSOW.resource_id) : null,
        resourceTypeId: newSOW.resource_type_id ? parseInt(newSOW.resource_type_id) : null,
        monthYear: newSOW.month_year,
        plannedDays: parseFloat(newSOW.planned_days),
        utilizationRate: parseFloat(newSOW.utilization_rate) || 0.9,
        dayRate: Math.round(parseFloat(newSOW.day_rate)),
        forecastRevenue: Math.round(parseFloat(newSOW.forecast_revenue)),
        frequency: newSOW.frequency,
        notes: newSOW.notes || ''
      };
      
      await sowAPI.create(sowData);
      setNewSOW({
        client_id: '',
        resource_id: '',
        resource_type_id: '',
        month_year: '',
        planned_days: 0,
        utilization_rate: 1.0,
        day_rate: 0,
        forecast_revenue: 0,
        frequency: 'monthly'
      });
      setShowAddForm(false);
      toast.success('SOW added successfully');
      loadGroupedDataWithFilters(); // Refresh data with current filters
    } catch (error) {
      console.error('Error adding SOW:', error);
      const errorMessage = errorHandler.getErrorMessage(error);
      toast.error('Failed to add SOW: ' + errorMessage);
    }
  };

  const handleDeleteSOW = async (sowId) => {
    if (!window.confirm('Are you sure you want to delete this SOW? This action cannot be undone.')) {
      return;
    }

    try {
      await sowAPI.delete(sowId);
      toast.success('SOW deleted successfully');
      loadGroupedDataWithFilters(); // Refresh data with current filters
    } catch (error) {
      console.error('Error deleting SOW:', error);
      const errorMessage = errorHandler.getErrorMessage(error);
      toast.error('Failed to delete SOW: ' + errorMessage);
    }
  };

  const getResourceTypeName = (resourceTypeId) => {
    const resourceType = resourceTypes.find(rt => rt.id === resourceTypeId);
    return resourceType ? resourceType.name : '-';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">SOW Management</h1>
        <div className="flex space-x-2">
          <button 
            onClick={() => setShowAddForm(!showAddForm)}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition-colors"
          >
            <Plus className="h-4 w-4 mr-2 inline" />
            Add SOW
          </button>
        </div>
      </div>

      {/* Add SOW Form */}
      {showAddForm && (
        <Card>
          <CardHeader>
            <CardTitle>Add New SOW</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Client</label>
                <select
                  value={newSOW.client_id}
                  onChange={(e) => handleClientChange(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select Client</option>
                  {clients.map(client => (
                    <option key={client.id} value={client.id}>{client.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Resource (Optional - for specific resource assignment)
                </label>
                <select
                  value={newSOW.resource_id}
                  onChange={(e) => handleResourceChange(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select Resource (Optional)</option>
                  {resources.map(resource => (
                    <option key={resource.id} value={resource.id}>
                      {resource.name} ({getResourceTypeName(resource.resource_type_id)})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Resource Type (Required if no specific resource selected)
                </label>
                <select
                  value={newSOW.resource_type_id}
                  onChange={(e) => handleResourceTypeChange(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select Resource Type</option>
                  {resourceTypes.map(type => (
                    <option key={type.id} value={type.id}>{type.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Month-Year</label>
                <select
                  value={newSOW.month_year}
                  onChange={(e) => setNewSOW({...newSOW, month_year: e.target.value})}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select Month</option>
                  {availableMonths.map(month => (
                    <option key={month.value} value={month.value}>{month.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Planned Days</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={newSOW.planned_days}
                  onChange={(e) => setNewSOW({...newSOW, planned_days: parseFloat(e.target.value) || 0})}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Utilization Rate (editable)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="1"
                  value={newSOW.utilization_rate}
                  onChange={(e) => setNewSOW({...newSOW, utilization_rate: parseFloat(e.target.value) ?? 1.0})}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  title="Utilization rate (1.0 = 100%, 0.9 = 90%). Default 100% for SOW."
                />
                <p className="text-xs text-gray-500 mt-1">
                  Default: 100%. Editable (e.g. 0.9 = 90% for annual leave).
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Day Rate</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={newSOW.day_rate}
                  onChange={(e) => setNewSOW({...newSOW, day_rate: parseFloat(e.target.value) || 0})}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Frequency</label>
                <select
                  value={newSOW.frequency}
                  onChange={(e) => setNewSOW({...newSOW, frequency: e.target.value})}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="monthly">Monthly</option>
                  <option value="weekly">Weekly</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Monthly Forecast Revenue (Auto-calculated)</label>
                <input
                  type="number"
                  value={newSOW.forecast_revenue}
                  readOnly
                  className="w-full p-2 border border-gray-300 rounded bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-2 mt-4">
              <button
                onClick={() => setShowAddForm(false)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAddSOW}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Add SOW
              </button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Filter by Month</label>
              <select
                value={filters.month}
                onChange={(e) => setFilters({...filters, month: e.target.value})}
                className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Months</option>
                {availableMonths.map(month => (
                  <option key={month.value} value={month.value}>{month.label}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Filter by Client</label>
              <select
                value={filters.client}
                onChange={(e) => setFilters({...filters, client: e.target.value})}
                className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Clients</option>
                {clients.map(client => (
                  <option key={client.id} value={client.id}>{client.name}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Filter by Resource Type</label>
              <select
                value={filters.resourceType}
                onChange={(e) => setFilters({...filters, resourceType: e.target.value})}
                className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Resource Types</option>
                {resourceTypes.map(type => (
                  <option key={type.id} value={type.id}>{type.name}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Filter by Resource</label>
              <select
                value={filters.resource}
                onChange={(e) => setFilters({...filters, resource: e.target.value})}
                className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Resources</option>
                {resources.map(resource => (
                  <option key={resource.id} value={resource.id}>{resource.name}</option>
                ))}
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* SOW Table */}
      <Card>
        <CardHeader>
          <CardTitle>SOW Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">Client</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">Month</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">Resource</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">Resource Type</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">Frequency</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">Planned Days</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">Utilization Rate</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">Adjusted Days</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">Day Rate</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">Forecast Revenue</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">Adjusted Revenue</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.map((item, index) => (
                  <React.Fragment key={`${item.client_id}-${item.month_year}-${item.resource_id}`}>
                    <tr className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4">
                                                     {item.client_name || 'Unknown Client'}
                      </td>
                      <td className="py-3 px-4">{item.month_year}</td>
                                             <td className="py-3 px-4">{item.person_name || '-'}</td>
                                             <td className="py-3 px-4">{item.resource_type_name || '-'}</td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          item.frequency === 'weekly' 
                            ? 'bg-blue-100 text-blue-800' 
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {item.frequency === 'weekly' ? 'Weekly' : 'Monthly'}
                        </span>
                      </td>
                      <td className="py-3 px-4">{parseFloat(item.planned_days).toFixed(2)}</td>
                      <td className="py-3 px-4">
                        {(parseFloat(item.utilization_rate || 0.9) * 100).toFixed(0)}%
                      </td>
                      <td className="py-3 px-4">
                        {parseFloat(item.adjusted_days || (item.planned_days * (item.utilization_rate || 0.9))).toFixed(2)}
                      </td>
                      <td className="py-3 px-4">{formatCurrency(item.day_rate)}</td>
                      <td className="py-3 px-4">{formatCurrency(item.forecast_revenue)}</td>
                      <td className="py-3 px-4">
                        {formatCurrency(item.adjusted_revenue || (item.adjusted_days || (item.planned_days * (item.utilization_rate || 0.9))) * item.day_rate)}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEditSOW(item)}
                            className="text-blue-600 hover:text-blue-900"
                            title="Edit SOW"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteSOW(item.id)}
                            className="text-red-600 hover:text-red-900"
                            title="Delete SOW"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                    

                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Edit SOW Modal */}
      {editingSOW && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Edit SOW</h3>
              <button onClick={handleCancelEdit} className="text-gray-500 hover:text-gray-700">
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Client</label>
                <select
                  value={editingSOW.client_id}
                  onChange={(e) => {
                    const clientId = e.target.value;
                    setEditingSOW(prev => ({ 
                      ...prev, 
                      client_id: clientId,
                      day_rate: getSowDayRate(clientId, prev.resource_id, prev.resource_type_id)
                    }));
                  }}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select Client</option>
                  {clients.map(client => (
                    <option key={client.id} value={client.id}>{client.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Resource</label>
                <select
                  value={editingSOW.resource_id}
                  onChange={(e) => {
                    const resourceId = e.target.value;
                    const selectedResource = resources.find(r => r.id === parseInt(resourceId));
                    if (selectedResource) {
                      setEditingSOW(prev => ({
                        ...prev,
                        resource_id: resourceId,
                        day_rate: getSowDayRate(prev.client_id, resourceId, selectedResource.resource_type_id),
                        resource_type_id: selectedResource.resource_type_id || ''
                      }));
                    } else {
                      setEditingSOW(prev => ({ 
                        ...prev, 
                        resource_id: resourceId,
                        day_rate: getSowDayRate(prev.client_id, resourceId, prev.resource_type_id)
                      }));
                    }
                  }}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select Resource</option>
                  {resources.map(resource => (
                    <option key={resource.id} value={resource.id}>{resource.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Resource Type</label>
                <select
                  value={editingSOW.resource_type_id}
                  onChange={(e) => {
                    const resourceTypeId = e.target.value;
                    setEditingSOW(prev => ({ 
                      ...prev, 
                      resource_type_id: resourceTypeId,
                      day_rate: getSowDayRate(prev.client_id, prev.resource_id, resourceTypeId)
                    }));
                  }}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select Resource Type</option>
                  {resourceTypes.map(type => (
                    <option key={type.id} value={type.id}>{type.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Month-Year</label>
                <select
                  value={editingSOW.month_year}
                  onChange={(e) => setEditingSOW({...editingSOW, month_year: e.target.value})}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select Month</option>
                  {availableMonths.map(month => (
                    <option key={month.value} value={month.value}>{month.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Planned Days</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={editingSOW.planned_days}
                  onChange={(e) => setEditingSOW({...editingSOW, planned_days: parseFloat(e.target.value) || 0})}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Utilization Rate (editable)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="1"
                  value={editingSOW.utilization_rate ?? 1.0}
                  onChange={(e) => setEditingSOW({...editingSOW, utilization_rate: parseFloat(e.target.value) ?? 1.0})}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  title="Utilization rate (1.0 = 100%, 0.9 = 90%)"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Editable per engagement. Default 100%.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Day Rate</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={editingSOW.day_rate}
                  onChange={(e) => setEditingSOW({...editingSOW, day_rate: parseFloat(e.target.value) || 0})}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Forecast Revenue</label>
                <input
                  type="number"
                  value={editingSOW.forecast_revenue}
                  onChange={(e) => setEditingSOW({...editingSOW, forecast_revenue: parseFloat(e.target.value) || 0})}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Frequency</label>
                <select
                  value={editingSOW.frequency}
                  onChange={(e) => setEditingSOW({...editingSOW, frequency: e.target.value})}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="monthly">Monthly</option>
                  <option value="weekly">Weekly</option>
                </select>
              </div>
            </div>

            <div className="flex justify-end space-x-2 mt-6">
              <button
                onClick={handleCancelEdit}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleSaveSOW(editingSOW)}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                <Save className="h-4 w-4 mr-2 inline" />
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SOWManagement; 